import { MCP_BRAND_SPY_TOOL_CATALOG } from "./mcp-brand-spy";
import {
  MCP_CREATIVE_PRODUCTION_OPERATION_POLICIES,
  MCP_CREATIVE_PRODUCTION_TOOL,
} from "./mcp-creative-production";
import { MCP_STATIC_ADS_CATALOG } from "./mcp-static-ads";
import { MCP_TOOL_CATALOG, MCP_PERFORMANCE_BRIEF_TOOLS } from "./mcp-contract";
import { MCP_SLIDESHOW_EXPRESS_CATALOG } from "./mcp-slideshow-express";

export const MCP_CONSOLE_CATEGORIES = [
  { id: "core", label: "Core" },
  { id: "performance", label: "Performance" },
  { id: "brand-spy", label: "Brand Spy" },
  { id: "static-ads", label: "Static Ads" },
  { id: "creative-production", label: "Creative Production" },
  { id: "slideshow", label: "Slideshow" },
] as const;

export type McpConsoleCategory = typeof MCP_CONSOLE_CATEGORIES[number]["id"];
export type McpConsoleToolKind = "read" | "write" | "approval";

type ToolDescriptor = {
  name: string;
  description: string;
  annotations: { readOnlyHint: boolean };
};

export const MCP_REGISTERED_TOOL_CATALOG: readonly ToolDescriptor[] = [
  ...MCP_TOOL_CATALOG,
  ...MCP_STATIC_ADS_CATALOG,
  ...MCP_BRAND_SPY_TOOL_CATALOG,
  ...((MCP_TOOL_CATALOG as ReadonlyArray<{ name: string }>).some(
    (tool) => tool.name === MCP_CREATIVE_PRODUCTION_TOOL.name,
  ) ? [] : [MCP_CREATIVE_PRODUCTION_TOOL]),
];

const performanceNames = new Set<string>(MCP_PERFORMANCE_BRIEF_TOOLS.map((tool) => tool.name));
const slideshowNames = new Set<string>(MCP_SLIDESHOW_EXPRESS_CATALOG.map((tool) => tool.name));
const approvalGatedNames = new Set<string>(
  MCP_CREATIVE_PRODUCTION_OPERATION_POLICIES.some((policy) => policy.confirmation !== "none")
    ? [MCP_CREATIVE_PRODUCTION_TOOL.name]
    : [],
);

function categoryFor(name: string): McpConsoleCategory {
  if (performanceNames.has(name)) return "performance";
  if (slideshowNames.has(name)) return "slideshow";
  if (name.startsWith("personaos_brand_spy_")) return "brand-spy";
  if (name.startsWith("personaos_static_ads_") || name.startsWith("personaos_creative_lineage_")) return "static-ads";
  if (name === MCP_CREATIVE_PRODUCTION_TOOL.name) return "creative-production";
  return "core";
}

export const MCP_CONSOLE_TOOLS = MCP_REGISTERED_TOOL_CATALOG.map((tool) => ({
  name: tool.name,
  description: tool.description,
  category: categoryFor(tool.name),
  kind: approvalGatedNames.has(tool.name)
    ? "approval"
    : tool.annotations.readOnlyHint
      ? "read"
      : "write",
})) satisfies ReadonlyArray<{
  name: string;
  description: string;
  category: McpConsoleCategory;
  kind: McpConsoleToolKind;
}>;

export function filterMcpConsoleTools(
  tools: typeof MCP_CONSOLE_TOOLS,
  search: string,
  category: "all" | McpConsoleCategory,
) {
  const query = search.trim().toLocaleLowerCase();
  return tools.filter((tool) => {
    if (category !== "all" && tool.category !== category) return false;
    if (!query) return true;
    return `${tool.name} ${tool.description} ${tool.category}`.toLocaleLowerCase().includes(query);
  });
}

export function getMcpConsoleStats(tools = MCP_CONSOLE_TOOLS) {
  return {
    total: tools.length,
    categories: new Set(tools.map((tool) => tool.category)).size,
    writeCapable: tools.filter((tool) => tool.kind !== "read").length,
    approvalGated: tools.filter((tool) => tool.kind === "approval").length,
  };
}
